import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, styled, Typography } from '@mui/material';
import DraggableList from 'components/DraggableList';
import {
  CarriedInterestDayCountFormat,
  CarriedInterestFeeAllocationMethod,
  OutsideCommitmentCapitalTreatment,
  WaterfallStructure,
} from 'further-types/lp/carry-settings';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column, Divider } from 'further-ui/components/Layout';
import {
  waterfallStructureDescriptions,
  waterfallStructureLabels,
} from 'further-ui/labels';
import { carrySettings } from 'further-ui/validations';
import { Controller, useForm } from 'react-hook-form';
import DayCountSelect from '../../DayCountSelect';
import { useCarrySettingsContext } from '../CarrySettingsContext';
import useOverallCarriedInterestSettings, {
  FormValues,
} from './hooks/useOverallCarriedInterestSettings';
import WaterfallStructureItem from './WaterfallStructureItem';

const MaxWidthColumn = styled(Column)(() => ({
  maxWidth: 400,
}));

const ItalicTerm = styled('i')({
  marginRight: '0.1em',
});

const StyledDraggableList = styled(DraggableList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

type Props = {
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    carrySettings: {
      dayCount: CarriedInterestDayCountFormat;
      outsideCommitmentCapitalTreatment: OutsideCommitmentCapitalTreatment;
      feeAllocationMethod: CarriedInterestFeeAllocationMethod;
      waterfallStructure: Array<WaterfallStructure>;
    };
  };
};

const OverallCarriedInterestSettings: React.FC<Props> = ({ vehicle }) => {
  const { setSaveButtonPending } = useCarrySettingsContext();
  const {
    feeAllocationMethodOptions,
    defaultWaterfallStructure,
    outsideCommitmentCapitalTreatmentOptions,
    updateSettings,
  } = useOverallCarriedInterestSettings({
    onMutate: () => setSaveButtonPending(true),
    onSettled: () => setSaveButtonPending(false),
  });
  const { control, handleSubmit, watch } = useForm({
    resolver: zodResolver(carrySettings.OverallCarriedInterestSettings),
    shouldFocusError: true,
    values: {
      ...vehicle.carrySettings,
      waterfallStructure:
        vehicle.carrySettings.waterfallStructure.length > 1
          ? vehicle.carrySettings.waterfallStructure
          : defaultWaterfallStructure,
    },
    criteriaMode: 'all',
  });

  const onSubmit = (data: FormValues) => {
    updateSettings({
      vehicleId: vehicle._id,
      ...data,
    });
  };

  const draggableWaterfallItems: Array<WaterfallStructure> = watch(
    'waterfallStructure',
  ).filter((item) => item !== WaterfallStructure.ExcessProceeds);

  return (
    <form id="carry-settings-form-overall" onSubmit={handleSubmit(onSubmit)}>
      <Column fullWidth spacing="lg">
        <FormField
          label="Preferred return day count"
          tooltip="This is the day count option used for calculating the preferred return across the fund vehicle."
          size="md"
        >
          <Controller
            name="dayCount"
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <DayCountSelect
                value={value}
                onChange={onChange}
                error={!!error}
              />
            )}
          />
        </FormField>
        <FormField
          label="Carried interest allocation method"
          size="md"
          tooltip={
            <Typography variant="inherit">
              <ItalicTerm>Investor-specific</ItalicTerm> calculates carry based
              on each investor individually, reflecting each LP’s specific terms
              and capital commitments.{' '}
              <ItalicTerm>Commitment pro-rata</ItalicTerm> calculates a fund
              level carry based on total commitment and splits it between
              investors (i.e. 10% of the total fund commitment equals 10% of the
              carry).
            </Typography>
          }
        >
          <Controller
            name="feeAllocationMethod"
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <BasicSelect
                size="small"
                fullWidth
                options={feeAllocationMethodOptions}
                error={!!error}
                value={value}
                onChange={(val) => onChange(val)}
              />
            )}
          />
        </FormField>
        <FormField label="Outside commitment capital treatment" size="md">
          <Controller
            name="outsideCommitmentCapitalTreatment"
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <BasicSelect
                size="small"
                fullWidth
                options={outsideCommitmentCapitalTreatmentOptions}
                error={!!error}
                value={value}
                onChange={(val) => onChange(val)}
              />
            )}
          />
        </FormField>
        <Divider />
        <FormField
          label="Waterfall priority"
          tooltip="Select the steps in the vehicle’s waterfall for use in distribution calculations."
        >
          <Controller
            name="waterfallStructure"
            control={control}
            render={({ fieldState: { error }, field: { onChange } }) => (
              <MaxWidthColumn spacing="sm">
                <StyledDraggableList
                  onReorder={({ orderedItemIds: newOrder }) => {
                    onChange([...newOrder, WaterfallStructure.ExcessProceeds]);
                  }}
                >
                  {draggableWaterfallItems.map((item, index) => (
                    <DraggableList.Item
                      id={item.toString()}
                      key={item.toString()}
                      withDragHandle
                    >
                      <WaterfallStructureItem
                        label={waterfallStructureLabels[item]}
                        description={waterfallStructureDescriptions[item]}
                        index={index}
                      />
                    </DraggableList.Item>
                  ))}
                </StyledDraggableList>
                <Divider />
                <WaterfallStructureItem
                  label={
                    waterfallStructureLabels[WaterfallStructure.ExcessProceeds]
                  }
                  description={
                    waterfallStructureDescriptions[
                      WaterfallStructure.ExcessProceeds
                    ]
                  }
                  fixed
                  index={draggableWaterfallItems.length}
                />
                {error && <Alert severity="error">{error.message}</Alert>}
              </MaxWidthColumn>
            )}
          />
        </FormField>
      </Column>
    </form>
  );
};

export default OverallCarriedInterestSettings;
